import { useEffect, useState, useCallback } from "react";
import { CalendarClock, Plus, X, Trash2, CheckCircle2, MessageCircle, Megaphone, FileSpreadsheet, Pencil, FileText, RotateCcw } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi } from "../../lib/download";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";
const today = new Date().toISOString().slice(0, 10);
const FILTERS = [{ v: "", l: "Tümü" }, { v: "aktif", l: "Aktif" }, { v: "geciken", l: "Vadesi Geçen" }, { v: "tamam", l: "Tamamlanan" }];
const PERIODS = [{ v: "gun", l: "Günlük" }, { v: "hafta", l: "Haftalık" }, { v: "ay", l: "Aylık" }, { v: "3ay", l: "3 Aylık" }];
const SOURCE_LBL = { plan: "Ödeme Planı", fatura: "Fatura", cari_vade: "Cari Vade", cek_senet: "Çek/Senet", pos_okc: "POS/ÖKC (net)", pazaryeri: "Pazaryeri (brüt)", kredi: "Kredi" };
const rangeFor = (p) => {
  const d = new Date();
  const iso = (x) => x.toISOString().slice(0, 10);
  if (p === "gun") return [iso(d), iso(d)];
  if (p === "hafta") { const e = new Date(d); e.setDate(e.getDate() + 6); return [iso(d), iso(e)]; }
  if (p === "ay") { const s = new Date(d.getFullYear(), d.getMonth(), 1); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0); return [iso(s), iso(e)]; }
  const e = new Date(d); e.setDate(e.getDate() + 90); return [iso(d), iso(e)];
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [caris, setCaris] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(null);
  const [creditForm, setCreditForm] = useState(null);
  const [pay, setPay] = useState(null);
  const [payBank, setPayBank] = useState("");
  const [reminders, setReminders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("planlar");
  const [period, setPeriod] = useState("ay");
  const [cp, setCp] = useState(null);
  const [tlData, setTlData] = useState(null);
  const [tlPeriod, setTlPeriod] = useState("ay");
  const [perf, setPerf] = useState(null);
  const [rpt, setRpt] = useState({ start: "", end: "", status: "", cari_id: "" });
  const [payAmount, setPayAmount] = useState("");
  const [editPlan, setEditPlan] = useState(null);

  useEffect(() => {
    if (view === "takvim") api.get("/admin/payment-plans/timeline", { params: { period: tlPeriod } }).then(({ data }) => setTlData(data)).catch(() => toast.error("Takvim yüklenemedi"));
    if (view === "performans") api.get("/admin/payment-plans/performance").then(({ data }) => setPerf(data)).catch(() => toast.error("Performans yüklenemedi"));
  }, [view, tlPeriod]);

  const downloadReport = async (kind) => {
    try {
      const params = {};
      if (rpt.start) params.start = rpt.start;
      if (rpt.end) params.end = rpt.end;
      if (rpt.status) params.status = rpt.status;
      if (rpt.cari_id) params.cari_id = rpt.cari_id;
      await downloadApi(`/admin/reports/odeme-planlari.${kind}`, `odeme-planlari-raporu.${kind}`, params);
    } catch { toast.error("Rapor oluşturulamadı"); }
  };

  const loadCp = useCallback(async () => {
    if (view !== "tahsilat") return;
    try {
      const [start, end] = rangeFor(period);
      const { data } = await api.get("/admin/collection-plan", { params: { start, end } });
      setCp(data);
    } catch { toast.error("Tahsilat planı yüklenemedi"); }
  }, [view, period]);

  useEffect(() => { loadCp(); }, [loadCp]);

  const downloadCpExcel = async () => {
    try {
      const [start, end] = rangeFor(period);
      await downloadApi("/admin/reports/tahsilat-plani.xlsx", `tahsilat-plani-${start}_${end}.xlsx`, { start, end });
    } catch { toast.error("Rapor oluşturulamadı"); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      const [{ data: pl }, { data: sum }, { data: cl }, { data: bk }, { data: rem }] = await Promise.all([
        api.get("/admin/payment-plans", { params }),
        api.get("/admin/payment-plans/summary"),
        api.get("/admin/cari"),
        api.get("/admin/bank-accounts"),
        api.get("/admin/reminders"),
      ]);
      setPlans(pl);
      setSummary(sum);
      setCaris(cl);
      setBanks(bk);
      setReminders(rem);
    } catch { toast.error("Planlar yüklenemedi"); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const newForm = () => setForm({ cari_id: "", description: "", source: "cari", installments: [{ due_date: today, amount: "" }] });
  const [gen, setGen] = useState({ start: today, interval: "aylik", count: 3, total: "" });
  const INTERVAL_LBL = { gunluk: "Günlük", haftalik: "Haftalık", aylik: "Aylık", "3aylik": "3 Aylık" };

  const generate = () => {
    const total = parseFloat(gen.total);
    const n = parseInt(gen.count);
    if (!total || total <= 0 || !n || n < 1 || n > 60) { toast.error("Geçerli toplam tutar ve taksit sayısı (1-60) girin"); return; }
    const d0 = new Date(gen.start || today);
    const rows = [];
    const per = Math.round((total / n) * 100) / 100;
    for (let i = 0; i < n; i++) {
      const d = new Date(d0);
      if (gen.interval === "gunluk") d.setDate(d.getDate() + i);
      else if (gen.interval === "haftalik") d.setDate(d.getDate() + i * 7);
      else if (gen.interval === "aylik") d.setMonth(d.getMonth() + i);
      else d.setMonth(d.getMonth() + i * 3);
      rows.push({ due_date: d.toISOString().slice(0, 10), amount: i === n - 1 ? Math.round((total - per * (n - 1)) * 100) / 100 : per });
    }
    setForm((f) => ({ ...f, installments: rows }));
    toast.success(`${n} taksit oluşturuldu (${INTERVAL_LBL[gen.interval]}) — dilerseniz satırları düzenleyin`);
  };
  const setInst = (i, k, v) => setForm((f) => { const arr = [...f.installments]; arr[i] = { ...arr[i], [k]: v }; return { ...f, installments: arr }; });
  const formTotal = form ? form.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) : 0;

  const save = async () => {
    if (!form.cari_id) { toast.error("Cari seçin"); return; }
    const installments = form.installments.filter((i) => i.due_date && parseFloat(i.amount) > 0).map((i) => ({ due_date: i.due_date, amount: parseFloat(i.amount) }));
    if (!installments.length) { toast.error("En az bir geçerli taksit girin"); return; }
    setBusy(true);
    try {
      await api.post("/admin/payment-plans", { cari_id: form.cari_id, description: form.description, source: form.source, installments });
      toast.success("Ödeme planı oluşturuldu");
      setForm(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const confirmPay = async () => {
    setBusy(true);
    try {
      const amt = parseFloat(payAmount) || pay.inst.amount;
      if (amt < pay.inst.amount - 0.005) {
        const { data } = await api.post(`/admin/payment-plans/${pay.plan.plan_id}/installments/${pay.inst.no}/partial-pay`, { amount: amt, bank_account_id: payBank });
        toast.success(`Kısmi ödeme işlendi — kalan: ${tl(data.remaining)} ₺`);
      } else {
        await api.post(`/admin/payment-plans/${pay.plan.plan_id}/installments/${pay.inst.no}/pay`, { bank_account_id: payBank });
        toast.success("Taksit ödendi olarak işlendi (cari'ye tahsilat yazıldı)");
      }
      setPay(null);
      setPayBank("");
      setPayAmount("");
      load();
    } catch (e) { toast.error(errText(e) || "İşlenemedi"); }
    finally { setBusy(false); }
  };

  const unpay = async (p, i) => {
    if (!window.confirm(`${p.cari_name} — ${i.no}. taksit ödemesi geri alınsın mı? Cari/banka bakiyeleri tersine çevrilir.`)) return;
    try {
      await api.post(`/admin/payment-plans/${p.plan_id}/installments/${i.no}/unpay`);
      toast.success("Ödeme geri alındı");
      load();
    } catch (e) { toast.error(errText(e) || "Geri alınamadı"); }
  };

  const openEdit = (p) => setEditPlan({
    plan_id: p.plan_id,
    description: p.description || "",
    installments: p.installments.filter((i) => i.status !== "odendi" && !(i.paid_amount > 0)).map((i) => ({ due_date: i.due_date, amount: i.amount })),
  });

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/payment-plans/${editPlan.plan_id}`, {
        description: editPlan.description,
        installments: editPlan.installments.map((i) => ({ due_date: i.due_date, amount: parseFloat(i.amount) || 0 })),
      });
      toast.success("Plan güncellendi");
      setEditPlan(null);
      load();
    } catch (e) { toast.error(errText(e) || "Güncellenemedi"); }
    finally { setBusy(false); }
  };

  const newCredit = () => setCreditForm({ bank_name: "", name: "", principal: "", interest_rate: "", count: 12, start: today });

  const creditSave = async () => {
    if (!creditForm.bank_name.trim()) { toast.error("Banka adı girin"); return; }
    const P = parseFloat(creditForm.principal);
    const n = parseInt(creditForm.count);
    if (!P || P <= 0 || !n || n < 1 || n > 360) { toast.error("Geçerli ana para ve taksit sayısı (1-360) girin"); return; }
    const r = (parseFloat(creditForm.interest_rate) || 0) / 100;
    const pay = r > 0 ? (P * r) / (1 - Math.pow(1 + r, -n)) : P / n;
    const per = Math.round(pay * 100) / 100;
    const totalPay = Math.round(pay * n * 100) / 100;
    const d0 = new Date(creditForm.start || today);
    const installments = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(d0);
      d.setMonth(d.getMonth() + i);
      installments.push({ due_date: d.toISOString().slice(0, 10), amount: i === n - 1 ? Math.round((totalPay - per * (n - 1)) * 100) / 100 : per });
    }
    setBusy(true);
    try {
      await api.post("/admin/payment-plans", {
        cari_id: "", source: "kredi",
        description: creditForm.name.trim() || `${creditForm.bank_name.trim()} kredisi`,
        bank_name: creditForm.bank_name.trim(), interest_rate: parseFloat(creditForm.interest_rate) || 0, principal: P,
        installments,
      });
      toast.success("Kredi planı oluşturuldu");
      setCreditForm(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const remindWhatsApp = async (item) => {
    const text = encodeURIComponent(`Sayın ${item.cari_name}, ${item.due_date} vadeli ${tl(item.amount)} TL tutarındaki ${item.no}. taksit ödemenizin vadesi ${item.days_overdue} gün önce geçmiştir. Ödemenizi hatırlatmak isteriz. — Ata Angora Doğal Gıdalar`);
    const digits = (item.phone || "").replace(/\D/g, "");
    const waPhone = digits.startsWith("0") ? `9${digits}` : digits;
    if (waPhone) window.open(`https://wa.me/${waPhone}?text=${text}`, "_blank");
    try {
      await api.post(`/admin/reminders/${item.plan_id}/${item.no}/done`);
      toast.success(waPhone ? "Hatırlatma gönderildi olarak işlendi" : "Telefon kayıtlı değil — hatırlatma işlendi");
      load();
    } catch { toast.error("İşlenemedi"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Plan silinsin mi? (Ödenmiş taksiti olan plan silinemez)")) return;
    try {
      await api.delete(`/admin/payment-plans/${p.plan_id}`);
      toast.success("Plan silindi");
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  return (
    <div data-testid="admin-plans-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Ödeme & Tahsilat Planları</h1>
          <p className="text-stone-500 text-sm mt-1">Taksitli tahsilat planları; ödenen taksit cari'ye ve seçilirse banka hesabına otomatik işlenir.</p>
        </div>
        <button data-testid="plan-new-button" onClick={newForm} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <Plus className="w-4 h-4" /> Yeni Plan
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["planlar", "Ödeme Planları"], ["kredi", "Kredi Takip"], ["takvim", "Takvim"], ["performans", "Performans"], ["rapor", "Rapor"], ["tahsilat", "Tahsilat Planı"]].map(([v, l]) => (
          <button key={v} data-testid={`plans-view-${v}`} onClick={() => setView(v)} className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${view === v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{l}</button>
        ))}
      </div>

      {view === "kredi" && (<>
        {(() => {
          const krediler = plans.filter((p) => p.source === "kredi");
          const kalan = krediler.reduce((s, p) => s + p.remaining, 0);
          const ay = today.slice(0, 7);
          const buAy = krediler.reduce((s, p) => s + p.installments.filter((i) => i.status !== "odendi" && i.due_date.startsWith(ay)).reduce((x, i) => x + i.amount, 0), 0);
          const aktif = krediler.filter((p) => !p.done).length;
          return (<>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
                <div data-testid="credit-summary-remaining" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Toplam Kalan Kredi Borcu</p><p className="text-2xl font-bold text-terra mt-1">{tl(kalan)} ₺</p></div>
                <div data-testid="credit-summary-month" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Bu Ay Ödenecek Taksit</p><p className="text-2xl font-bold text-stone-900 mt-1">{tl(buAy)} ₺</p></div>
                <div data-testid="credit-summary-count" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Aktif Kredi</p><p className="text-2xl font-bold text-forest mt-1">{aktif}</p></div>
              </div>
              <button data-testid="credit-new-button" onClick={newCredit} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Kredi</button>
            </div>
            {krediler.length === 0 && <div className="bg-white rounded-2xl border border-stone-200/80 p-10 text-center text-stone-400 text-sm">Kayıtlı kredi yok — "Yeni Kredi" ile banka kredinizi taksitlendirin.</div>}
            <div className="space-y-4">
              {krediler.map((p) => {
                const pct = p.total > 0 ? Math.round((p.paid_total / p.total) * 100) : 0;
                const sonraki = p.installments.find((i) => i.status !== "odendi");
                return (
                  <div key={p.plan_id} data-testid={`credit-row-${p.plan_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-stone-900">{p.bank_name || p.cari_name} {p.done && <span className="text-xs text-emerald-700 font-bold">(Kapandı)</span>}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{p.description}{p.principal ? ` • Ana para: ${tl(p.principal)} ₺` : ""}{p.interest_rate ? ` • Aylık faiz: %${p.interest_rate}` : ""} • {p.installments.length} taksit{sonraki ? ` • Sonraki vade: ${sonraki.due_date}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-stone-600">Kalan: <b className="text-terra">{tl(p.remaining)} ₺</b> / {tl(p.total)} ₺</p>
                        {!p.done && <button data-testid={`credit-delete-${p.plan_id}`} onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                      </div>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden mt-3"><div className={`h-full rounded-full ${pct === 100 ? "bg-emerald-600" : "bg-forest"}`} style={{ width: `${pct}%` }} /></div>
                    <div className="mt-3 overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
                      <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300"><th className="py-2 pr-3 pl-2">#</th><th className="py-2 pr-3">Vade</th><th className="py-2 pr-3 text-right">Tutar</th><th className="py-2 pr-3">Durum</th><th className="py-2 pr-2 text-right">İşlem</th></tr></thead>
                      <tbody>
                        {p.installments.map((i) => (
                          <tr key={i.no} className="border-b border-stone-100">
                            <td className="py-2 pr-3 pl-2 text-stone-500">{i.no}</td>
                            <td className="py-2 pr-3">{i.due_date}</td>
                            <td className="py-2 pr-3 text-right font-semibold">{tl(i.amount)} ₺{i.paid_amount > 0 && i.status !== "odendi" && <span className="block text-[11px] font-normal text-emerald-700">ödenen: {tl(i.paid_amount)} ₺</span>}</td>
                            <td className="py-2 pr-3">{i.status === "odendi" ? <span className="text-[11px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Ödendi</span> : (i.due_date < today ? <span className="text-[11px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Gecikti</span> : <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Bekliyor</span>)}</td>
                            <td className="py-2 pr-2 text-right">{i.status !== "odendi" ? <button data-testid={`credit-pay-${p.plan_id}-${i.no}`} onClick={() => { setPay({ plan: p, inst: i }); setPayAmount(""); }} className="text-xs font-bold bg-forest hover:bg-forest-dark text-white px-3 py-1.5 rounded-lg transition-colors">Öde</button> : <button data-testid={`credit-unpay-${p.plan_id}-${i.no}`} onClick={() => unpay(p, i)} className="text-xs font-semibold text-stone-400 hover:text-terra underline">Geri Al</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </div>
                );
              })}
            </div>
          </>);
        })()}
      </>)}

      {view === "planlar" && (<>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div data-testid="plans-summary-pending" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Bekleyen Tahsilat</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{tl(summary.pending_total)} ₺</p>
          </div>
          <div data-testid="plans-summary-overdue" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Vadesi Geçen</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{tl(summary.overdue_total)} ₺</p>
            <p className="text-xs text-stone-400 mt-0.5">{summary.overdue_count} taksit</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Bu Ay Tahsil Edilen</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{tl(summary.paid_this_month)} ₺</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Plan Sayısı</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{summary.plan_count}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.v} data-testid={`plan-filter-${f.v || "all"}`} onClick={() => setFilter(f.v)} className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors ${filter === f.v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{f.l}</button>
        ))}
      </div>

      {reminders && reminders.items.length > 0 && (
        <div data-testid="reminders-panel" className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><Megaphone className="w-4 h-4" /> Hatırlatma Merkezi — {reminders.items.length} vadesi geçen taksit</p>
            <p data-testid="reminders-last-scan" className="text-[11px] text-red-500">{reminders.last_scan ? `Son otomatik tarama: ${reminders.last_scan.at.slice(0, 16).replace("T", " ")}` : "Otomatik tarama henüz çalışmadı (her sabah 08:00)"}</p>
          </div>
          <div className="mt-3 space-y-2">
            {reminders.items.map((r) => (
              <div key={`${r.plan_id}-${r.no}`} data-testid={`reminder-item-${r.plan_id}-${r.no}`} className="bg-white rounded-xl border border-red-100 px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold text-stone-900">{r.cari_name} <span className="text-xs font-normal text-stone-400">• {r.no}. taksit</span></p>
                  <p className="text-xs text-stone-500">Vade {r.due_date} — <b className="text-red-600">{r.days_overdue} gün gecikti</b>{r.last_reminded_at && ` • Son hatırlatma: ${r.last_reminded_at.slice(0, 10)}`}</p>
                </div>
                <p className="text-sm font-bold text-stone-900">{tl(r.amount)} ₺</p>
                <button data-testid={`reminder-wa-${r.plan_id}-${r.no}`} onClick={() => remindWhatsApp(r)} className="flex items-center gap-1.5 text-xs font-semibold bg-[#25D366] hover:bg-[#1fb857] text-white px-3.5 py-2 rounded-xl transition-colors">
                  <MessageCircle className="w-3.5 h-3.5" /> {r.phone ? "WhatsApp Hatırlat" : "Hatırlatıldı İşaretle"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {plans.filter((p) => p.source !== "kredi").map((p) => {
          const pct = p.total > 0 ? Math.round((p.paid_total / p.total) * 100) : 0;
          return (
            <div key={p.plan_id} data-testid={`plan-row-${p.plan_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-stone-900">{p.cari_name} {p.done && <span className="text-xs text-emerald-700 font-bold">(Tamamlandı)</span>} {p.source && <span data-testid={`plan-source-${p.plan_id}`} className="text-[10px] font-bold bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded ml-1 align-middle">{SOURCE_LBL[p.source] || p.source}</span>}</p>
                  {p.description && <p className="text-xs text-stone-500 mt-0.5">{p.description}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm text-stone-600"><b className="text-stone-900">{tl(p.paid_total)} ₺</b> / {tl(p.total)} ₺</p>
                  {!p.done && <button data-testid={`plan-edit-${p.plan_id}`} onClick={() => openEdit(p)} className="p-1.5 hover:bg-stone-100 rounded-lg"><Pencil className="w-4 h-4 text-stone-500" /></button>}
                  {!p.done && <button data-testid={`plan-delete-${p.plan_id}`} onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                </div>
              </div>
              <div className="h-2 bg-stone-100 rounded-full mt-3 overflow-hidden">
                <div className="h-full bg-forest rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
                    <th className="py-1.5 pr-3">#</th><th className="py-1.5 pr-3">Vade</th><th className="py-1.5 pr-3 text-right">Tutar</th><th className="py-1.5 pr-3">Durum</th><th className="py-1.5 text-right">İşlem</th>
                  </tr></thead>
                  <tbody>
                    {p.installments.map((i) => {
                      const overdue = i.status === "bekliyor" && i.due_date < today;
                      return (
                        <tr key={i.no} className={`border-t border-stone-100 ${overdue ? "bg-red-50/60" : ""}`}>
                          <td className="py-2 pr-3 text-stone-500">{i.no}</td>
                          <td className={`py-2 pr-3 ${overdue ? "text-red-700 font-semibold" : "text-stone-700"}`}>{i.due_date}</td>
                          <td className="py-2 pr-3 text-right font-medium">{tl(i.amount)} ₺</td>
                          <td className="py-2 pr-3">
                            {i.status === "odendi"
                              ? <span className="text-xs font-semibold text-emerald-700">Ödendi {(i.paid_at || "").slice(0, 10)} {i.paid_via && `• ${i.paid_via}`}</span>
                              : overdue ? <span className="text-xs font-semibold text-red-600">Vadesi Geçti</span> : <span className="text-xs text-stone-500">Bekliyor</span>}
                          </td>
                          <td className="py-2 text-right">
                            {i.status === "bekliyor" && (
                              <button data-testid={`plan-pay-${p.plan_id}-${i.no}`} onClick={() => { setPay({ plan: p, inst: i }); setPayBank(""); setPayAmount(String(i.amount)); }} className="inline-flex items-center gap-1 text-xs font-semibold text-forest hover:bg-forest/5 px-2.5 py-1.5 rounded-lg transition-colors">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Ödendi İşaretle
                              </button>
                            )}
                            {i.status === "odendi" && (
                              <button data-testid={`plan-unpay-${p.plan_id}-${i.no}`} onClick={() => unpay(p, i)} className="inline-flex items-center gap-1 text-xs font-semibold text-terra hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors">
                                <RotateCcw className="w-3.5 h-3.5" /> Geri Al
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        {plans.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-400">
            <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-40" />Ödeme planı bulunamadı
          </div>
        )}
      </div>
      </>)}

      {view === "tahsilat" && (
        <div data-testid="collection-plan" className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            {PERIODS.map((p) => (
              <button key={p.v} data-testid={`cp-period-${p.v}`} onClick={() => setPeriod(p.v)} className={`px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${period === p.v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{p.l}</button>
            ))}
            {cp && <span className="text-xs text-stone-400">{cp.start} → {cp.end}</span>}
            <button data-testid="cp-excel" onClick={downloadCpExcel} className="ml-auto flex items-center gap-1.5 text-xs font-semibold border border-forest text-forest hover:bg-forest/5 px-3.5 py-2 rounded-xl transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel Rapor</button>
          </div>
          {cp && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div data-testid="cp-gross" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Brüt Beklenen</p><p className="text-2xl font-bold text-stone-900 mt-1">{tl(cp.totals.gross)} ₺</p></div>
                <div data-testid="cp-commission" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Toplam Komisyon</p><p className="text-2xl font-bold text-amber-700 mt-1">-{tl(cp.totals.commission)} ₺</p></div>
                <div data-testid="cp-net" className="bg-forest text-cream rounded-2xl p-5 shadow-sm"><p className="text-xs uppercase tracking-wide text-cream/60">Net Tahsilat</p><p className="text-2xl font-bold mt-1">{tl(cp.totals.net)} ₺</p></div>
              </div>
              {Object.keys(cp.by_source).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(cp.by_source).map(([k, v]) => (
                    <span key={k} data-testid={`cp-source-${k}`} className="bg-white border border-stone-200 rounded-xl px-3.5 py-2 text-xs text-stone-700"><b>{SOURCE_LBL[k] || k}</b> ({v.count} kayıt): <span className="font-bold text-forest">{tl(v.net)} ₺</span>{v.commission > 0 && <span className="text-amber-700"> (−{tl(v.commission)} kom.)</span>}</span>
                  ))}
                </div>
              )}
              <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
                    <th className="px-5 py-3">Tarih</th><th className="px-5 py-3">Kaynak</th><th className="px-5 py-3">Açıklama</th><th className="px-5 py-3 text-right">Brüt</th><th className="px-5 py-3 text-right">Komisyon</th><th className="px-5 py-3 text-right">Net</th>
                  </tr></thead>
                  <tbody>
                    {cp.items.map((it, i) => (
                      <tr key={`${it.ref}-${i}`} data-testid={`cp-row-${i}`} className="border-b border-stone-100">
                        <td className="px-5 py-3 text-stone-600 whitespace-nowrap">{it.date}</td>
                        <td className="px-5 py-3"><span className={`px-2 py-1 rounded-lg text-xs font-medium ${it.source === "pos_okc" ? "bg-amber-100 text-amber-800" : it.source === "cek_senet" ? "bg-sky-100 text-sky-800" : it.source === "pazaryeri" ? "bg-purple-100 text-purple-800" : "bg-stone-100 text-stone-700"}`}>{SOURCE_LBL[it.source] || it.source}</span></td>
                        <td className="px-5 py-3 text-stone-700">{it.label}</td>
                        <td className="px-5 py-3 text-right">{tl(it.gross)} ₺</td>
                        <td className="px-5 py-3 text-right text-amber-700">{it.commission ? `-${tl(it.commission)}` : "—"}</td>
                        <td className="px-5 py-3 text-right font-bold text-forest">{tl(it.net)} ₺</td>
                      </tr>
                    ))}
                    {cp.items.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-stone-400">Bu dönemde beklenen tahsilat yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {view === "takvim" && (
        <div data-testid="plans-timeline" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {[["hafta", "Haftalık"], ["ay", "Aylık"], ["3ay", "3 Aylık"]].map(([v, l]) => (
              <button key={v} data-testid={`tl-period-${v}`} onClick={() => setTlPeriod(v)} className={`px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${tlPeriod === v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{l}</button>
            ))}
            {tlData && <span className="text-xs text-stone-400">{tlData.start} → {tlData.end}</span>}
          </div>
          {tlData && Object.keys(tlData.groups).length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-400">Bu dönemde ödeme/tahsilat kaydı yok</div>
          )}
          {tlData && Object.entries(tlData.groups).map(([k, items]) => (
            <div key={k} data-testid={`tl-group-${k}`} className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 bg-cream-dark/50 border-b border-stone-100">
                <h3 className="text-sm font-bold text-stone-800">{SOURCE_LBL[k] || k} <span className="text-xs font-normal text-stone-400">({items.length} kayıt)</span></h3>
                <p className="text-sm font-bold text-forest">{tl(tlData.totals[k])} ₺</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={`${it.plan_id}-${it.no}-${idx}`} className={`border-b border-stone-50 ${it.status === "gecikti" ? "bg-red-50/60" : ""}`}>
                        <td className="px-5 py-2.5 text-stone-600 whitespace-nowrap">{it.date}</td>
                        <td className="px-5 py-2.5 font-medium text-stone-800">{it.cari}</td>
                        <td className="px-5 py-2.5 text-stone-500">{it.label}</td>
                        <td className="px-5 py-2.5 text-right font-semibold">{tl(it.amount)} ₺{it.paid_amount > 0 && it.status !== "odendi" && <span className="text-xs text-amber-700"> (kısmi {tl(it.paid_amount)})</span>}</td>
                        <td className="px-5 py-2.5 text-right">
                          <span className={`text-[11px] font-bold px-2 py-1 rounded-lg ${it.status === "odendi" ? "bg-emerald-100 text-emerald-800" : it.status === "gecikti" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-500"}`}>
                            {it.status === "odendi" ? "ÖDENDİ" : it.status === "gecikti" ? "GECİKTİ" : "BEKLİYOR"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "performans" && perf && (
        <div data-testid="plans-performance" className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div data-testid="perf-rate" className="bg-forest text-cream rounded-2xl p-5 shadow-sm"><p className="text-xs uppercase tracking-wide text-cream/60">Tahsilat Oranı</p><p className="text-3xl font-bold mt-1">%{perf.collection_rate}</p><p className="text-xs text-cream/60 mt-1">{tl(perf.total_paid)} / {tl(perf.total_planned)} ₺</p></div>
            <div data-testid="perf-delay" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Ortalama Gecikme</p><p className="text-3xl font-bold text-stone-900 mt-1">{perf.avg_delay_days} gün</p></div>
            <div data-testid="perf-overdue" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Vadesi Geçen Bekleyen</p><p className="text-3xl font-bold text-red-600 mt-1">{tl(perf.overdue_pending)} ₺</p></div>
            <div data-testid="perf-month" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Bu Ay Tahsilat</p><p className="text-2xl font-bold text-emerald-700 mt-1">{tl(perf.paid_this_month)} ₺</p></div>
            <div data-testid="perf-last-month" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Geçen Ay Tahsilat</p><p className="text-2xl font-bold text-stone-700 mt-1">{tl(perf.paid_last_month)} ₺</p></div>
            <div data-testid="perf-plans" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Plan Sayısı</p><p className="text-2xl font-bold text-stone-900 mt-1">{perf.plan_count}</p></div>
          </div>
        </div>
      )}

      {view === "rapor" && (
        <div data-testid="plans-report" className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm space-y-4">
          <div>
            <h3 className="font-semibold text-stone-900">Profesyonel Ödeme Planı Raporu</h3>
            <p className="text-xs text-stone-500 mt-0.5">Tarih aralığı, cari ve durum filtreli PDF/Excel çıktısı — plan özetleri ve taksit detaylarıyla.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div><label className="text-xs font-semibold text-stone-600 block mb-1">Başlangıç</label><input data-testid="rpt-start" type="date" className={inputCls} value={rpt.start} onChange={(e) => setRpt({ ...rpt, start: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-stone-600 block mb-1">Bitiş</label><input data-testid="rpt-end" type="date" className={inputCls} value={rpt.end} onChange={(e) => setRpt({ ...rpt, end: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-stone-600 block mb-1">Cari</label>
              <select data-testid="rpt-cari" className={inputCls} value={rpt.cari_id} onChange={(e) => setRpt({ ...rpt, cari_id: e.target.value })}>
                <option value="">Tümü</option>
                {caris.map((c) => <option key={c.cari_id} value={c.cari_id}>{c.name}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-stone-600 block mb-1">Durum</label>
              <select data-testid="rpt-status" className={inputCls} value={rpt.status} onChange={(e) => setRpt({ ...rpt, status: e.target.value })}>
                <option value="">Tümü</option>
                <option value="aktif">Aktif</option>
                <option value="geciken">Vadesi Geçen</option>
                <option value="tamam">Tamamlanan</option>
              </select></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="rpt-pdf" onClick={() => downloadReport("pdf")} className="flex items-center gap-1.5 text-xs font-semibold bg-forest hover:bg-forest-dark text-white px-4 py-2.5 rounded-xl transition-colors"><FileText className="w-3.5 h-3.5" /> PDF Rapor İndir</button>
            <button data-testid="rpt-xlsx" onClick={() => downloadReport("xlsx")} className="flex items-center gap-1.5 text-xs font-semibold border border-forest text-forest hover:bg-forest/5 px-4 py-2.5 rounded-xl transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel Rapor İndir</button>
          </div>
        </div>
      )}

      {creditForm && (
        <div data-testid="credit-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCreditForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Yeni Kredi</h3>
              <button data-testid="credit-form-close" onClick={() => setCreditForm(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            {(() => {
              const cP = parseFloat(creditForm.principal) || 0;
              const cN = parseInt(creditForm.count) || 0;
              const cR = (parseFloat(creditForm.interest_rate) || 0) / 100;
              const cPay = cP > 0 && cN > 0 ? (cR > 0 ? (cP * cR) / (1 - Math.pow(1 + cR, -cN)) : cP / cN) : 0;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* SOL: kredi bilgileri */}
                  <div className="space-y-3 sm:pr-6 sm:border-r sm:border-stone-200">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Kredi Bilgileri</p>
                    <div><label className="text-xs font-semibold text-stone-600">Banka *</label><input data-testid="credit-bank" className={inputCls} value={creditForm.bank_name} onChange={(e) => setCreditForm({ ...creditForm, bank_name: e.target.value })} placeholder="ör. Ziraat Bankası" /></div>
                    <div><label className="text-xs font-semibold text-stone-600">Kredi Adı / Açıklama</label><input data-testid="credit-name" className={inputCls} value={creditForm.name} onChange={(e) => setCreditForm({ ...creditForm, name: e.target.value })} placeholder="ör. İşletme kredisi" /></div>
                    <div><label className="text-xs font-semibold text-stone-600">Ana Para ₺ *</label><input data-testid="credit-principal" type="number" min="0" step="0.01" className={inputCls} value={creditForm.principal} onChange={(e) => setCreditForm({ ...creditForm, principal: e.target.value })} /></div>
                    <div><label className="text-xs font-semibold text-stone-600">Aylık Faiz % (0 = faizsiz)</label><input data-testid="credit-rate" type="number" min="0" step="0.01" className={inputCls} value={creditForm.interest_rate} onChange={(e) => setCreditForm({ ...creditForm, interest_rate: e.target.value })} /></div>
                  </div>
                  {/* SAĞ: taksit planı */}
                  <div className="space-y-3">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Taksit Planı</p>
                    <div><label className="text-xs font-semibold text-stone-600">Taksit Sayısı</label><input data-testid="credit-count" type="number" min="1" max="360" className={inputCls} value={creditForm.count} onChange={(e) => setCreditForm({ ...creditForm, count: e.target.value })} /></div>
                    <div><label className="text-xs font-semibold text-stone-600">İlk Taksit Tarihi</label><input data-testid="credit-start" type="date" className={inputCls} value={creditForm.start} onChange={(e) => setCreditForm({ ...creditForm, start: e.target.value })} /></div>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-1">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Önizleme</p>
                      <p className="flex justify-between text-sm"><span className="text-stone-600">Aylık Taksit</span><b data-testid="credit-preview-monthly" className="text-stone-900">{tl(cPay)} ₺</b></p>
                      <p className="flex justify-between text-sm"><span className="text-stone-600">Toplam Geri Ödeme</span><b data-testid="credit-preview-total" className="text-forest">{tl(cPay * cN)} ₺</b></p>
                    </div>
                  </div>
                </div>
              );
            })()}
            <button data-testid="credit-submit" onClick={creditSave} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Krediyi Kaydet"}</button>
          </div>
        </div>
      )}

      {form && (
        <div data-testid="plan-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Yeni Ödeme Planı</h3>
              <button data-testid="plan-form-close" onClick={() => setForm(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SOL: plan bilgileri */}
              <div className="space-y-3 lg:pr-6 lg:border-r lg:border-stone-200">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Plan Bilgileri</p>
                <div><label className="text-xs font-semibold text-stone-600">Cari *</label>
                  <select data-testid="plan-cari-select" className={inputCls} value={form.cari_id} onChange={(e) => setForm({ ...form, cari_id: e.target.value })}>
                    <option value="">Seçin...</option>
                    {caris.map((c) => <option key={c.cari_id} value={c.cari_id}>{c.name}</option>)}
                  </select></div>
                <div><label className="text-xs font-semibold text-stone-600">Açıklama</label>
                  <input data-testid="plan-form-desc" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="ör. Eylül toptan satış — 3 taksit" /></div>
                <div><label className="text-xs font-semibold text-stone-600">Kaynak</label>
                  <select data-testid="plan-form-source" className={inputCls} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    <option value="cari">Cari Hesap</option>
                    <option value="pazaryeri">Pazaryeri</option>
                    <option value="pos_okc">POS / ÖKC Dönüşü</option>
                    <option value="diger">Diğer</option>
                  </select></div>
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-2">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400 mb-1">Özet</p>
                  <p className="text-sm font-bold text-stone-900" data-testid="plan-form-total">Toplam: {tl(formTotal)} ₺</p>
                  <p className="text-xs text-stone-500 mt-1">{form.installments.length} taksit</p>
                </div>
              </div>
              {/* SAĞ: taksitler */}
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Taksitler</p>
                <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-stone-500 px-1"><span>Vade</span><span>Tutar ₺</span></div>
                <div className="bg-forest/5 border border-forest/15 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-forest">Hızlı Plan Oluştur (Günlük / Haftalık / Aylık / 3 Aylık)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="plan-gen-start" type="date" className={inputCls} value={gen.start} onChange={(e) => setGen({ ...gen, start: e.target.value })} />
                    <select data-testid="plan-gen-interval" className={inputCls} value={gen.interval} onChange={(e) => setGen({ ...gen, interval: e.target.value })}>
                      <option value="gunluk">Günlük</option>
                      <option value="haftalik">Haftalık</option>
                      <option value="aylik">Aylık</option>
                      <option value="3aylik">3 Aylık</option>
                    </select>
                    <input data-testid="plan-gen-count" type="number" min="1" max="60" className={inputCls} value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} placeholder="Taksit" />
                    <input data-testid="plan-gen-total" type="number" min="0" step="0.01" className={inputCls} value={gen.total} onChange={(e) => setGen({ ...gen, total: e.target.value })} placeholder="Toplam ₺" />
                  </div>
                  <button type="button" data-testid="plan-gen-button" onClick={generate} className="w-full text-xs font-bold bg-forest hover:bg-forest-dark text-white py-2.5 rounded-xl transition-colors">Taksitleri Oluştur</button>
                </div>
                {form.installments.map((i, idx) => (                  <div key={idx} className="grid grid-cols-2 gap-2 items-center">
                    <input data-testid={`plan-inst-date-${idx}`} type="date" className={inputCls} value={i.due_date} onChange={(e) => setInst(idx, "due_date", e.target.value)} />
                    <div className="flex gap-2 items-center">
                      <input data-testid={`plan-inst-amount-${idx}`} type="number" min="0" step="0.01" className={inputCls} value={i.amount} onChange={(e) => setInst(idx, "amount", e.target.value)} />
                      {form.installments.length > 1 && <button data-testid={`plan-inst-remove-${idx}`} onClick={() => setForm((f) => ({ ...f, installments: f.installments.filter((_, j) => j !== idx) }))} className="p-2 hover:bg-red-50 rounded-lg shrink-0"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                    </div>
                  </div>
                ))}
                <button data-testid="plan-inst-add" onClick={() => setForm((f) => ({ ...f, installments: [...f.installments, { due_date: today, amount: "" }] }))} className="flex items-center gap-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg transition-colors">+ Taksit Ekle</button>
              </div>
            </div>
            <button data-testid="plan-submit" onClick={save} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Planı Kaydet"}</button>
          </div>
        </div>
      )}

      {editPlan && (
        <div data-testid="plan-edit-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditPlan(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Planı Düzenle</h3>
              <button data-testid="plan-edit-close" onClick={() => setEditPlan(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-stone-400 mb-3">Ödenmiş/kısmi ödenmiş taksitler korunur; yalnızca bekleyen taksitler düzenlenebilir.</p>
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-stone-600">Açıklama</label>
                <input data-testid="plan-edit-desc" className={inputCls} value={editPlan.description} onChange={(e) => setEditPlan({ ...editPlan, description: e.target.value })} /></div>
              {editPlan.installments.map((i, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2 items-center">
                  <input data-testid={`plan-edit-date-${idx}`} type="date" className={inputCls} value={i.due_date} onChange={(e) => setEditPlan((f) => { const a = [...f.installments]; a[idx] = { ...a[idx], due_date: e.target.value }; return { ...f, installments: a }; })} />
                  <div className="flex gap-2 items-center">
                    <input data-testid={`plan-edit-amount-${idx}`} type="number" min="0" step="0.01" className={inputCls} value={i.amount} onChange={(e) => setEditPlan((f) => { const a = [...f.installments]; a[idx] = { ...a[idx], amount: e.target.value }; return { ...f, installments: a }; })} />
                    {editPlan.installments.length > 1 && <button data-testid={`plan-edit-remove-${idx}`} onClick={() => setEditPlan((f) => ({ ...f, installments: f.installments.filter((_, j) => j !== idx) }))} className="p-2 hover:bg-red-50 rounded-lg shrink-0"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                  </div>
                </div>
              ))}
              <button data-testid="plan-edit-add" onClick={() => setEditPlan((f) => ({ ...f, installments: [...f.installments, { due_date: today, amount: "" }] }))} className="text-xs font-semibold text-forest hover:underline">+ Taksit Ekle</button>
            </div>
            <button data-testid="plan-edit-save" onClick={saveEdit} disabled={busy} className="mt-4 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}</button>
          </div>
        </div>
      )}

      {pay && (
        <div data-testid="plan-pay-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPay(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Taksit Ödemesi</h3>
              <button data-testid="plan-pay-close" onClick={() => setPay(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-stone-700">{pay.plan.cari_name} — {pay.inst.no}. taksit, vade {pay.inst.due_date}</p>
            <p className="text-2xl font-bold text-forest mt-2">{tl(pay.inst.amount)} ₺</p>
            <div className="mt-3"><label className="text-xs font-semibold text-stone-600">Tahsil Edilen Tutar ₺ (kısmi ödeme için azaltın)</label>
              <input data-testid="plan-pay-amount" type="number" min="0" step="0.01" className={inputCls} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} /></div>
            <div className="mt-4"><label className="text-xs font-semibold text-stone-600">Banka Hesabına İşle (opsiyonel)</label>
              <select data-testid="plan-pay-bank-select" className={inputCls} value={payBank} onChange={(e) => setPayBank(e.target.value)}>
                <option value="">Yalnızca cari'ye işle</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name}{b.commission_rate > 0 && ` (%${b.commission_rate} komisyon)`}</option>)}
              </select></div>
            <p className="text-xs text-stone-400 mt-2">Tutar cari bakiyesinden tahsilat olarak düşülür; banka seçilirse hesaba tahsilat (komisyonlu ise net) işlenir.</p>
            <button data-testid="plan-pay-confirm" onClick={confirmPay} disabled={busy} className="mt-4 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "İşleniyor..." : "Ödemeyi Onayla"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
